/* Sub-Batch 3AJ — Validation Dashboard
 *
 * Surfaces BKAgriD's cross-checks against published peer-reviewed
 * datasets. This is the user-facing answer to "is the math right?"
 * — every claim links to its primary citation.
 *
 * The first module (Molecular QTL) validates against the canonical
 * R/qtl `hyper` dataset (Sugiyama et al. 2001). Future modules
 * (AMMI/GGE, augmented design, Cullis H²) will be added as the
 * three-dataset criterion in /memory/IDEAS.md is satisfied.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertCircle, ExternalLink, Loader2, RefreshCw } from 'lucide-react';

const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin) || process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const COLORS = {
  green900: '#1B4332',
  green500: '#2D6A4F',
  green100: '#D8F3DC',
  amber: '#F59E0B',
  amber50: '#FEF3C7',
  amber900: '#92400E',
  red: '#DC2626',
  red50: '#FEE2E2',
  textMedium: '#475569',
  border: '#E5E7EB',
};

function StatusBadge({ pass, label }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 font-body"
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: '3px 9px',
        borderRadius: 99,
        background: pass ? COLORS.green100 : COLORS.red50,
        color: pass ? COLORS.green900 : COLORS.red,
        border: `1px solid ${pass ? COLORS.green500 : COLORS.red}`,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
      data-testid={`validation-status-${pass ? 'pass' : 'fail'}`}
    >
      {pass ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
      {label}
    </span>
  );
}

function ComparisonRow({ row }) {
  const obs = typeof row.observed === 'number' ? row.observed.toFixed(3) : String(row.observed);
  const pub = typeof row.published_value === 'number' ? row.published_value.toFixed(3) : String(row.published_value);
  const tol = typeof row.tolerance === 'number' ? `± ${row.tolerance.toFixed(2)}` : '';
  const delta = typeof row.delta === 'number' ? (row.delta >= 0 ? `+${row.delta.toFixed(3)}` : row.delta.toFixed(3)) : '';
  return (
    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{row.quantity}</td>
      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 13 }}>{obs}</td>
      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 13, color: COLORS.textMedium }}>
        {pub} <span style={{ color: COLORS.textMedium, fontSize: 11 }}>{tol} {row.units}</span>
      </td>
      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 12, color: row.pass ? COLORS.green500 : COLORS.red }}>{delta}</td>
      <td style={{ padding: '8px 10px' }}><StatusBadge pass={row.pass} label={row.pass ? 'PASS' : 'FAIL'} /></td>
    </tr>
  );
}

function SanityRow({ row }) {
  return (
    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{row.check}</td>
      <td style={{ padding: '8px 10px', color: COLORS.textMedium, fontSize: 13 }}>{row.expected}</td>
      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 13 }}>{String(row.observed)}</td>
      <td style={{ padding: '8px 10px' }}><StatusBadge pass={row.pass} label={row.pass ? 'PASS' : 'FAIL'} /></td>
    </tr>
  );
}

export default function ValidationDashboard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState('SIM');
  const [doQc, setDoQc] = useState(false);

  // Module 2 — AMMI/GGE on Yan winter wheat
  const [ammiReport, setAmmiReport] = useState(null);
  const [ammiLoading, setAmmiLoading] = useState(false);
  const [ammiError, setAmmiError] = useState(null);

  // Module 3 — Augmented Block Design on Petersen 1994 wheat
  const [augReport, setAugReport] = useState(null);
  const [augLoading, setAugLoading] = useState(false);
  const [augError, setAugError] = useState(null);

  // Module 4 — Polyploid (Allohexaploid Wheat) QTL on Gao 2015
  const [polyReport, setPolyReport] = useState(null);
  const [polyLoading, setPolyLoading] = useState(false);
  const [polyError, setPolyError] = useState(null);

  // Module 5 — Polyploid (Autotetraploid Potato) QTL on B2721
  const [potReport, setPotReport] = useState(null);
  const [potLoading, setPotLoading] = useState(false);
  const [potError, setPotError] = useState(null);

  // Module 6 — Augmented MET Scenario B on Belamkar 2018 winter wheat
  const [sbReport, setSbReport] = useState(null);
  const [sbLoading, setSbLoading] = useState(false);
  const [sbError, setSbError] = useState(null);

  const fetchReport = async (forceRefresh = false) => {
    setLoading(true); setError(null);
    try {
      const url = `${API}/validation/qtl-hyper?method=${method}&n_perm=1000&do_marker_qc=${doQc}`;
      const resp = await axios.get(url, { withCredentials: true });
      setReport(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAmmiReport = async () => {
    setAmmiLoading(true); setAmmiError(null);
    try {
      const resp = await axios.get(`${API}/validation/ammi-gge-yan`, { withCredentials: true });
      setAmmiReport(resp.data);
    } catch (e) {
      setAmmiError(e.response?.data?.detail || e.message);
    } finally {
      setAmmiLoading(false);
    }
  };

  const fetchAugReport = async () => {
    setAugLoading(true); setAugError(null);
    try {
      const resp = await axios.get(`${API}/validation/augmented-petersen`, { withCredentials: true });
      setAugReport(resp.data);
    } catch (e) {
      setAugError(e.response?.data?.detail || e.message);
    } finally {
      setAugLoading(false);
    }
  };

  const fetchPolyReport = async () => {
    setPolyLoading(true); setPolyError(null);
    try {
      const resp = await axios.get(`${API}/validation/polyploid-wheat-gao2015`, { withCredentials: true });
      setPolyReport(resp.data);
    } catch (e) {
      setPolyError(e.response?.data?.detail || e.message);
    } finally {
      setPolyLoading(false);
    }
  };

  const fetchPotReport = async () => {
    setPotLoading(true); setPotError(null);
    try {
      const resp = await axios.get(`${API}/validation/polyploid-potato-b2721`, { withCredentials: true });
      setPotReport(resp.data);
    } catch (e) {
      setPotError(e.response?.data?.detail || e.message);
    } finally {
      setPotLoading(false);
    }
  };

  const fetchSbReport = async () => {
    setSbLoading(true); setSbError(null);
    try {
      const resp = await axios.get(`${API}/validation/augmented-met-scenario-b`, { withCredentials: true });
      setSbReport(resp.data);
    } catch (e) {
      setSbError(e.response?.data?.detail || e.message);
    } finally {
      setSbLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport(); }, [method, doQc]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAmmiReport(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAugReport(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPolyReport(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPotReport(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSbReport(); }, []);

  return (
    <div className="font-body" style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px' }} data-testid="validation-page">
      {/* Header */}
      <div style={{ borderLeft: `4px solid ${COLORS.green500}`, paddingLeft: 16, marginBottom: 24 }}>
        <h1 className="font-display" style={{ fontSize: 28, color: COLORS.green900, marginBottom: 4 }}>
          Validation Dashboard
        </h1>
        <p style={{ fontSize: 15, color: COLORS.textMedium }}>
          Cross-checks BKAgriD&apos;s analysis modules against published peer-reviewed datasets.
          Every claim links to its primary citation. One-way validation: BKAgriD output vs published numerical values.
        </p>
      </div>

      {/* Status badge */}
      <div
        className="font-body"
        style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 24,
          background: COLORS.amber50, borderLeft: `3px solid ${COLORS.amber}`,
          color: COLORS.amber900, fontSize: 13,
        }}
        data-testid="validation-criterion-status"
      >
        <strong>Unparking criterion 1 status:</strong>{' '}
        <span style={{ fontWeight: 600 }}>6 of 6 modules validated.</span>{' '}
        Module 1 (Molecular QTL — Broman <em>hyper</em>), Module 2 (AMMI/GGE — Yan 2000
        winter wheat), Module 3 (Augmented Block Design — Petersen 1994 durum
        wheat), Module 4 (Polyploid / allohexaploid bread wheat — Gao 2015
        Zhou&nbsp;8425B/Chinese&nbsp;Spring, CIMMYT co-authored), Module 5 (Polyploid /
        AUTOtetraploid potato — B2721 Atlantic × B1829-5, da Silva Pereira 2020), and
        Module 6 (Augmented MET Scenario B — Federer check-anchored combining over sites,
        Belamkar 2018 Nebraska winter wheat) all ship
        with locked numerical comparisons against published references.{' '}
        <a
          href="https://github.com/kbroman/qtl"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: COLORS.green500, textDecoration: 'underline' }}
        >R/qtl source <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /></a>
      </div>

      {/* Module 1 card */}
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 12,
        padding: 24, marginBottom: 24, background: 'white',
      }} data-testid="validation-module-qtl">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 className="font-display" style={{ fontSize: 20, color: COLORS.green900, marginBottom: 2 }}>
              Module 1 — Molecular QTL Mapping (Phase 2)
            </h2>
            <p style={{ fontSize: 13, color: COLORS.textMedium }}>
              Validated against the canonical R/qtl <code>hyper</code> dataset
            </p>
          </div>
          {report && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge
                pass={report.overall_pass}
                label={`${report.n_passed}/${report.n_total_checks} ${report.overall_pass ? 'PASS' : 'CHECKS'}`}
              />
              <button
                onClick={() => fetchReport(true)}
                disabled={loading}
                title="Re-run validation"
                style={{
                  padding: 6, border: `1px solid ${COLORS.border}`, background: 'white',
                  borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
                }}
                data-testid="validation-refresh-btn"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </button>
            </div>
          )}
        </div>

        {/* Method + QC controls */}
        <div style={{ display: 'flex', gap: 16, marginTop: 14, marginBottom: 18, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: COLORS.textMedium }}>
            <span style={{ marginRight: 6 }}>Scan method:</span>
            <select
              value={method}
              onChange={e => setMethod(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${COLORS.border}` }}
              data-testid="validation-method-select"
            >
              <option value="SIM">SIM (single-QTL scanone)</option>
              <option value="CIM">CIM (multi-QTL drop-one)</option>
            </select>
          </label>
          <label style={{ fontSize: 13, color: COLORS.textMedium, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={doQc}
              onChange={e => setDoQc(e.target.checked)}
              data-testid="validation-qc-checkbox"
            />
            Apply marker QC filter (BKAgriD default: missingness ≤ 10%)
          </label>
        </div>

        {error && (
          <div style={{ padding: 12, background: COLORS.red50, color: COLORS.red, borderRadius: 6, marginBottom: 16 }}>
            <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            {error}
          </div>
        )}

        {loading && !report && (
          <div style={{ padding: 24, textAlign: 'center', color: COLORS.textMedium }}>
            <Loader2 size={20} className="animate-spin" style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            Running validation ({method}, n_perm=1000) — typically 30-60 seconds...
          </div>
        )}

        {report && (
          <>
            {/* Dataset provenance */}
            <div style={{ background: '#F8FAFC', padding: 14, borderRadius: 8, marginBottom: 18, borderLeft: `3px solid ${COLORS.green500}` }} data-testid="validation-dataset-card">
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>Dataset:</strong> {report.dataset?.dataset_full_name}</div>
                <div><strong>Cross type:</strong> {report.dataset?.cross_type}</div>
                <div><strong>Individuals × Markers × Chromosomes:</strong> {report.dataset?.n_individuals} × {report.dataset?.n_markers} × {report.dataset?.n_chromosomes}</div>
                <div><strong>Trait:</strong> {report.dataset?.trait_description} ({report.dataset?.trait_name})</div>
                <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Citation:</strong> {report.dataset?.primary_citation}
                </div>
              </div>
            </div>

            {/* Sanity invariants */}
            {report.sanity_invariants?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-sanity-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Sanity invariants — must hold for ANY correct implementation
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px' }}>Check</th>
                      <th style={{ padding: '8px 10px' }}>Expected</th>
                      <th style={{ padding: '8px 10px' }}>Observed</th>
                      <th style={{ padding: '8px 10px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.sanity_invariants.map((s, i) => <SanityRow key={i} row={s} />)}
                  </tbody>
                </table>
              </div>
            )}

            {/* Numerical comparisons */}
            <div style={{ marginBottom: 18 }} data-testid="validation-comparisons-section">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                Numerical comparison vs published reference values
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Quantity</th>
                    <th style={{ padding: '8px 10px' }}>BKAgriD</th>
                    <th style={{ padding: '8px 10px' }}>Published</th>
                    <th style={{ padding: '8px 10px' }}>Δ</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.comparisons.map((c, i) => <ComparisonRow key={i} row={c} />)}
                </tbody>
              </table>
            </div>

            {/* Detected QTLs */}
            {report.bkagrid_output?.qtl_peaks?.length > 0 && (
              <details style={{ marginBottom: 18 }}>
                <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Detected QTL peaks ({report.bkagrid_output.qtl_peaks.length})
                </summary>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Marker</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Chr</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Position (cM)</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>LOD</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>Support interval</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left' }}>PVE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.bkagrid_output.qtl_peaks.map((q, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{q.marker}</td>
                        <td style={{ padding: '6px 10px' }}>{q.chromosome}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.position_cM).toFixed(1)}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.lod_score).toFixed(2)}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>
                          {q.lod_ci_lo_cM != null && q.lod_ci_hi_cM != null
                            ? `[${Number(q.lod_ci_lo_cM).toFixed(1)}, ${Number(q.lod_ci_hi_cM).toFixed(1)}]`
                            : '—'}
                        </td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>
                          {q.pve_percent != null ? `${Number(q.pve_percent).toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {/* Limitations */}
            <div style={{ background: COLORS.amber50, padding: 14, borderRadius: 8, borderLeft: `3px solid ${COLORS.amber}`, fontSize: 12, color: COLORS.amber900 }} data-testid="validation-limitations">
              <strong>Honesty disclaimer:</strong>
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20, lineHeight: 1.6 }}>
                {(report.limitations || []).map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Module 2 — AMMI / GGE on Yan winter wheat */}
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 12,
        padding: 24, marginBottom: 24, background: 'white',
      }} data-testid="validation-module-ammi-gge">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 className="font-display" style={{ fontSize: 20, color: COLORS.green900, marginBottom: 2 }}>
              Module 2 — AMMI / GGE Biplot (MET analysis)
            </h2>
            <p style={{ fontSize: 13, color: COLORS.textMedium }}>
              Validated against the canonical Yan &amp; Kang (2003) Ontario winter-wheat dataset
            </p>
          </div>
          {ammiReport && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge
                pass={ammiReport.overall_pass}
                label={`${ammiReport.n_passed}/${ammiReport.n_total_checks} ${ammiReport.overall_pass ? 'PASS' : 'CHECKS'}`}
              />
              <button
                onClick={fetchAmmiReport}
                disabled={ammiLoading}
                title="Re-run AMMI/GGE validation"
                style={{
                  padding: 6, border: `1px solid ${COLORS.border}`, background: 'white',
                  borderRadius: 6, cursor: ammiLoading ? 'wait' : 'pointer',
                }}
                data-testid="validation-ammi-refresh-btn"
              >
                {ammiLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </button>
            </div>
          )}
        </div>

        {ammiError && (
          <div style={{ padding: 12, background: COLORS.red50, color: COLORS.red, borderRadius: 6, marginBottom: 16 }}>
            <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            {ammiError}
          </div>
        )}

        {ammiLoading && !ammiReport && (
          <div style={{ padding: 24, textAlign: 'center', color: COLORS.textMedium }}>
            <Loader2 size={20} className="animate-spin" style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            Running AMMI + GGE on yan.winterwheat — typically &lt; 5 seconds...
          </div>
        )}

        {ammiReport && (
          <>
            {/* Dataset provenance */}
            <div style={{ background: '#F8FAFC', padding: 14, borderRadius: 8, marginBottom: 18, borderLeft: `3px solid ${COLORS.green500}` }} data-testid="validation-ammi-dataset-card">
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>Dataset:</strong> {ammiReport.dataset?.dataset_full_name}</div>
                <div><strong>Genotypes × Environments:</strong> {ammiReport.dataset?.n_genotypes} × {ammiReport.dataset?.n_environments} ({ammiReport.dataset?.n_observations} obs)</div>
                <div><strong>Trait:</strong> {ammiReport.dataset?.trait_description}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMedium }}>
                  <strong>GGE citation:</strong> {ammiReport.dataset?.primary_citation}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Book reference:</strong> {ammiReport.dataset?.book_citation}
                </div>
              </div>
            </div>

            {/* Sanity invariants */}
            {ammiReport.sanity_invariants?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-ammi-sanity-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Sanity invariants — must hold for ANY correct AMMI/GGE implementation
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px' }}>Check</th>
                      <th style={{ padding: '8px 10px' }}>Expected</th>
                      <th style={{ padding: '8px 10px' }}>Observed</th>
                      <th style={{ padding: '8px 10px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ammiReport.sanity_invariants.map((s, i) => <SanityRow key={i} row={s} />)}
                  </tbody>
                </table>
              </div>
            )}

            {/* Numerical comparisons */}
            <div style={{ marginBottom: 18 }} data-testid="validation-ammi-comparisons-section">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                Numerical comparison vs published reference values
                <span style={{ fontWeight: 400, fontSize: 12, color: COLORS.textMedium, marginLeft: 8 }}>
                  (GGE → Yan 2000 Crop Sci 40:597-605; AMMI → Yan &amp; Tinker 2006 Can J Plant Sci 86:623-645)
                </span>
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Quantity</th>
                    <th style={{ padding: '8px 10px' }}>BKAgriD</th>
                    <th style={{ padding: '8px 10px' }}>Published</th>
                    <th style={{ padding: '8px 10px' }}>Δ</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ammiReport.comparisons.map((c, i) => <ComparisonRow key={i} row={c} />)}
                </tbody>
              </table>
            </div>

            {/* Output summary */}
            <details style={{ marginBottom: 18 }}>
              <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                BKAgriD raw output (GGE + AMMI partitions)
              </summary>
              <div style={{ background: '#FAFAFA', padding: 12, borderRadius: 6, marginTop: 8, fontSize: 12, fontFamily: 'monospace', lineHeight: 1.6 }}>
                <strong>GGE:</strong> PC1 = {ammiReport.bkagrid_output?.gge?.pc1_variance_pct}%, PC2 = {ammiReport.bkagrid_output?.gge?.pc2_variance_pct}%, sum = {ammiReport.bkagrid_output?.gge?.pc1_plus_pc2_pct}% · best = {ammiReport.bkagrid_output?.gge?.best_genotype} · most-discriminating env = {ammiReport.bkagrid_output?.gge?.most_discriminating_env}<br/>
                <strong>AMMI:</strong> IPCA1 = {ammiReport.bkagrid_output?.ammi?.ipca1_pct_of_gxe}%, IPCA2 = {ammiReport.bkagrid_output?.ammi?.ipca2_pct_of_gxe}%, sum = {ammiReport.bkagrid_output?.ammi?.ipca1_plus_ipca2_pct}% · selected = {ammiReport.bkagrid_output?.ammi?.selected_model} · grand mean = {ammiReport.bkagrid_output?.ammi?.grand_mean} t/ha · top genotype = {ammiReport.bkagrid_output?.ammi?.top_genotype_by_mean}<br/>
                <strong>SS partition:</strong> G = {ammiReport.bkagrid_output?.ammi?.ss_partition?.Genotype?.toFixed(3)}, E = {ammiReport.bkagrid_output?.ammi?.ss_partition?.Environment?.toFixed(3)}, G×E = {ammiReport.bkagrid_output?.ammi?.ss_partition?.GxE_Interaction?.toFixed(3)}, Total = {ammiReport.bkagrid_output?.ammi?.ss_partition?.Total?.toFixed(3)}
              </div>
            </details>

            {/* Limitations */}
            <div style={{ background: COLORS.amber50, padding: 14, borderRadius: 8, borderLeft: `3px solid ${COLORS.amber}`, fontSize: 12, color: COLORS.amber900 }} data-testid="validation-ammi-limitations">
              <strong>Honesty disclaimer:</strong>
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20, lineHeight: 1.6 }}>
                {(ammiReport.limitations || []).map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Module 3 — Augmented Block Design on Petersen (1994) durum wheat */}
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 12,
        padding: 24, marginBottom: 24, background: 'white',
      }} data-testid="validation-module-augmented">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 className="font-display" style={{ fontSize: 20, color: COLORS.green900, marginBottom: 2 }}>
              Module 3 — Augmented Block Design (Federer 1956 ABD)
            </h2>
            <p style={{ fontSize: 13, color: COLORS.textMedium }}>
              Validated against the Petersen (1994) durum-wheat canonical worked example;
              also functions as the Sub-Batch 3X 2&times; inflation regression sentinel.
            </p>
          </div>
          {augReport && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge
                pass={augReport.overall_pass}
                label={`${augReport.n_passed}/${augReport.n_total_checks} ${augReport.overall_pass ? 'PASS' : 'CHECKS'}`}
              />
              <button
                onClick={fetchAugReport}
                disabled={augLoading}
                title="Re-run Augmented Design validation"
                style={{
                  padding: 6, border: `1px solid ${COLORS.border}`, background: 'white',
                  borderRadius: 6, cursor: augLoading ? 'wait' : 'pointer',
                }}
                data-testid="validation-aug-refresh-btn"
              >
                {augLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </button>
            </div>
          )}
        </div>

        {augError && (
          <div style={{ padding: 12, background: COLORS.red50, color: COLORS.red, borderRadius: 6, marginBottom: 16 }}>
            <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            {augError}
          </div>
        )}

        {augLoading && !augReport && (
          <div style={{ padding: 24, textAlign: 'center', color: COLORS.textMedium }}>
            <Loader2 size={20} className="animate-spin" style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            Running ABD analysis on petersen.wheat — typically &lt; 2 seconds...
          </div>
        )}

        {augReport && (
          <>
            {/* Dataset provenance */}
            <div style={{ background: '#F8FAFC', padding: 14, borderRadius: 8, marginBottom: 18, borderLeft: `3px solid ${COLORS.green500}` }} data-testid="validation-aug-dataset-card">
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>Dataset:</strong> {augReport.dataset?.dataset_full_name}</div>
                <div><strong>Design:</strong> {augReport.dataset?.design}</div>
                <div><strong>Structure:</strong> {augReport.dataset?.n_test_genotypes} new selections + {augReport.dataset?.n_checks} checks ({(augReport.dataset?.checks || []).join(', ')}) × {augReport.dataset?.n_blocks} blocks = {augReport.dataset?.n_observations} plots</div>
                <div><strong>Trait:</strong> {augReport.dataset?.trait_description}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Primary citation:</strong> {augReport.dataset?.primary_citation}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Archive:</strong>{' '}
                  <a href={augReport.dataset?.archive_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.green500, textDecoration: 'underline' }}>
                    archive.org PDF <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </a>
                </div>
              </div>
            </div>

            {/* Sanity invariants */}
            {augReport.sanity_invariants?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-aug-sanity-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Sanity invariants — must hold for ANY correct ABD analysis
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px' }}>Check</th>
                      <th style={{ padding: '8px 10px' }}>Expected</th>
                      <th style={{ padding: '8px 10px' }}>Observed</th>
                      <th style={{ padding: '8px 10px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {augReport.sanity_invariants.map((s, i) => (
                      <SanityRow key={i} row={{
                        ...s,
                        observed: typeof s.observed === 'object' && s.observed !== null
                          ? JSON.stringify(s.observed)
                          : s.observed,
                      }} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* LOCKED + STRUCTURAL comparisons */}
            <div style={{ marginBottom: 18 }} data-testid="validation-aug-locked-section">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                LOCKED &amp; STRUCTURAL comparisons
                <span style={{ fontWeight: 400, fontSize: 12, color: COLORS.textMedium, marginLeft: 8 }}>
                  (five published values [MSE / Blocks SS / Checks SS / Error SS / S_s]; nine
                  structural invariants of the ABD design; two Sub-Batch 3X heritability sentinels)
                </span>
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Quantity</th>
                    <th style={{ padding: '8px 10px' }}>BKAgriD</th>
                    <th style={{ padding: '8px 10px' }}>Reference</th>
                    <th style={{ padding: '8px 10px' }}>Δ</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {augReport.comparisons.filter(c => ['LOCKED', 'STRUCTURAL'].includes(c.status)).map((c, i) => (
                    <ComparisonRow key={i} row={c} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* PROVISIONAL comparisons */}
            <div style={{ marginBottom: 18 }} data-testid="validation-aug-provisional-section">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.amber900, marginBottom: 8 }}>
                PROVISIONAL comparisons{' '}
                <span style={{ fontWeight: 400, fontSize: 12, color: COLORS.textMedium }}>
                  — derivable from the data but not yet primary-source-confirmed against the printed Table 5.7
                </span>
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: COLORS.amber50, textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Quantity</th>
                    <th style={{ padding: '8px 10px' }}>BKAgriD</th>
                    <th style={{ padding: '8px 10px' }}>Reference</th>
                    <th style={{ padding: '8px 10px' }}>Δ</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {augReport.comparisons.filter(c => c.status === 'PROVISIONAL').map((c, i) => (
                    <ComparisonRow key={i} row={c} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Findings */}
            {augReport.findings?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-aug-findings-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Findings &amp; methodological notes
                </h3>
                {augReport.findings.map((f, i) => (
                  <div key={i} style={{
                    background: f.severity === 'INFO' ? '#EFF6FF' : COLORS.amber50,
                    padding: 12, borderRadius: 6, marginBottom: 8,
                    borderLeft: `3px solid ${f.severity === 'INFO' ? '#3B82F6' : COLORS.amber}`,
                    fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      [{f.severity}] {f.title}
                    </div>
                    <div style={{ color: COLORS.textMedium, marginBottom: 4 }}>{f.detail}</div>
                    {f.implication && (
                      <div style={{ fontSize: 12, fontStyle: 'italic', color: COLORS.textMedium }}>
                        <strong>Implication:</strong> {f.implication}
                      </div>
                    )}
                    {f.sentinel_value && (
                      <div style={{ fontSize: 12, fontStyle: 'italic', color: COLORS.green900, marginTop: 4 }}>
                        <strong>Sentinel value:</strong> {f.sentinel_value}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* BKAgriD raw output */}
            <details style={{ marginBottom: 18 }}>
              <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                BKAgriD raw output (ANOVA + variance components)
              </summary>
              <div style={{ background: '#FAFAFA', padding: 12, borderRadius: 6, marginTop: 8, fontSize: 12, fontFamily: 'monospace', lineHeight: 1.6 }}>
                <strong>ANOVA (check-only intra-block):</strong>{' '}
                Blocks SS = {augReport.bkagrid_output?.blocks_ss?.toFixed(2)} (DF={augReport.bkagrid_output?.df_blocks}),
                Checks SS = {augReport.bkagrid_output?.checks_ss?.toFixed(2)} (DF={augReport.bkagrid_output?.df_checks}),
                Error SS = {augReport.bkagrid_output?.error_ss?.toFixed(2)} (DF={augReport.bkagrid_output?.df_error}){'\u00A0'}<br/>
                <strong>MSE = {augReport.bkagrid_output?.mse?.toFixed(4)}</strong>{' '}
                (Petersen 1994 published: 91,103){'\u00A0'}<br/>
                <strong>Variance:</strong> Vg = {augReport.bkagrid_output?.Vg?.toFixed(2)},
                Ve = {augReport.bkagrid_output?.Ve?.toFixed(2)},
                CV % = {augReport.bkagrid_output?.cv_percent?.toFixed(2)}<br/>
                <strong>H²_broad_sense (reported)</strong> = {augReport.bkagrid_output?.H2_broad_sense_reported?.toFixed(4)} {'\u00A0\u00A0'}
                <strong>H²_test_basis</strong> = {augReport.bkagrid_output?.H2_test_basis_computed?.toFixed(4)} {'\u00A0\u00A0'}
                <strong>H²_check_basis (aux)</strong> = {augReport.bkagrid_output?.H2_check_basis_aux?.toFixed(4)}<br/>
                <strong>Best check:</strong> {augReport.bkagrid_output?.best_check} {'\u00A0\u00A0'}
                <strong>Best test entry:</strong> {augReport.bkagrid_output?.best_test_entry}
              </div>
            </details>

            {/* Limitations */}
            <div style={{ background: COLORS.amber50, padding: 14, borderRadius: 8, borderLeft: `3px solid ${COLORS.amber}`, fontSize: 12, color: COLORS.amber900 }} data-testid="validation-aug-limitations">
              <strong>Honesty disclaimer:</strong>
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20, lineHeight: 1.6 }}>
                {(augReport.limitations || []).map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Module 4 — Polyploid (Allohexaploid Wheat) QTL on Gao (2015) */}
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 12,
        padding: 24, marginBottom: 24, background: 'white',
      }} data-testid="validation-module-polyploid-wheat">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 className="font-display" style={{ fontSize: 20, color: COLORS.green900, marginBottom: 2 }}>
              Module 4 — Polyploid (Allohexaploid Wheat) QTL Mapping
            </h2>
            <p style={{ fontSize: 13, color: COLORS.textMedium }}>
              Validated against Gao et al. (2015) Zhou&nbsp;8425B/Chinese&nbsp;Spring bread-wheat RILs —{' '}
              <strong>CIMMYT co-authored</strong>, open-access (CC BY)
            </p>
          </div>
          {polyReport && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge
                pass={polyReport.overall_pass}
                label={`${polyReport.n_passed}/${polyReport.n_total_checks} ${polyReport.overall_pass ? 'PASS' : 'CHECKS'}`}
              />
              <button
                onClick={fetchPolyReport}
                disabled={polyLoading}
                title="Re-run Polyploid Wheat validation"
                style={{
                  padding: 6, border: `1px solid ${COLORS.border}`, background: 'white',
                  borderRadius: 6, cursor: polyLoading ? 'wait' : 'pointer',
                }}
                data-testid="validation-poly-refresh-btn"
              >
                {polyLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </button>
            </div>
          )}
        </div>

        {polyError && (
          <div style={{ padding: 12, background: COLORS.red50, color: COLORS.red, borderRadius: 6, marginBottom: 16 }}>
            <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            {polyError}
          </div>
        )}

        {polyLoading && !polyReport && (
          <div style={{ padding: 24, textAlign: 'center', color: COLORS.textMedium }}>
            <Loader2 size={20} className="animate-spin" style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            Recovering the flagship 6A QTL on the Gao 2015 wheat map — typically &lt; 3 seconds...
          </div>
        )}

        {polyReport && (
          <>
            {/* Dataset provenance */}
            <div style={{ background: '#F8FAFC', padding: 14, borderRadius: 8, marginBottom: 18, borderLeft: `3px solid ${COLORS.green500}` }} data-testid="validation-poly-dataset-card">
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>Dataset:</strong> {polyReport.dataset?.dataset_full_name}</div>
                <div><strong>Crop:</strong> {polyReport.dataset?.crop}</div>
                <div><strong>Population:</strong> {polyReport.dataset?.population} · {polyReport.dataset?.genotyping}</div>
                <div><strong>Map:</strong> {polyReport.dataset?.map}</div>
                <div><strong>Method:</strong> {polyReport.dataset?.method}</div>
                <div><strong>Institute:</strong> {polyReport.dataset?.institute}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Citation:</strong> {polyReport.dataset?.primary_citation}{' '}
                  <a href={polyReport.dataset?.article_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.green500, textDecoration: 'underline' }}>
                    doi:{polyReport.dataset?.doi} <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </a>
                </div>
              </div>
            </div>

            {/* Wheat QTL map figure */}
            {polyReport.bkagrid_output?.wheat_qtl_map_png && (
              <div style={{ marginBottom: 18 }} data-testid="validation-poly-map-figure">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Allohexaploid wheat QTL map — 21 chromosomes (7 homoeologous groups × A/B/D subgenomes)
                </h3>
                <img
                  src={`data:image/png;base64,${polyReport.bkagrid_output.wheat_qtl_map_png}`}
                  alt="Wheat QTL map across A/B/D subgenomes"
                  style={{ width: '100%', maxWidth: 980, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}
                />
              </div>
            )}

            {/* Sanity invariants */}
            {polyReport.sanity_invariants?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-poly-sanity-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Sanity invariants — must hold for ANY correct wheat QTL map
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px' }}>Check</th>
                      <th style={{ padding: '8px 10px' }}>Expected</th>
                      <th style={{ padding: '8px 10px' }}>Observed</th>
                      <th style={{ padding: '8px 10px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {polyReport.sanity_invariants.map((s, i) => <SanityRow key={`poly-sanity-${i}`} row={s} />)}
                  </tbody>
                </table>
              </div>
            )}

            {/* Numerical comparisons */}
            <div style={{ marginBottom: 18 }} data-testid="validation-poly-comparisons-section">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                Recovery &amp; structural comparisons vs Gao 2015 published values
                <span style={{ fontWeight: 400, fontSize: 12, color: COLORS.textMedium, marginLeft: 8 }}>
                  (flagship QTKW.caas-6A.1 recovered by Haley-Knott interval mapping)
                </span>
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Quantity</th>
                    <th style={{ padding: '8px 10px' }}>BKAgriD</th>
                    <th style={{ padding: '8px 10px' }}>Published</th>
                    <th style={{ padding: '8px 10px' }}>Δ</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {polyReport.comparisons.map((c, i) => <ComparisonRow key={`poly-cmp-${i}`} row={c} />)}
                </tbody>
              </table>
            </div>

            {/* Published QTL catalogue */}
            <details style={{ marginBottom: 18 }} data-testid="validation-poly-qtl-catalogue">
              <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                Published QTL catalogue ({polyReport.bkagrid_output?.published_qtls?.length || 0}) — Gao 2015 Table 3
              </summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px' }}>QTL</th>
                    <th style={{ padding: '6px 10px' }}>Trait</th>
                    <th style={{ padding: '6px 10px' }}>Chr</th>
                    <th style={{ padding: '6px 10px' }}>Pos (cM)</th>
                    <th style={{ padding: '6px 10px' }}>LOD</th>
                    <th style={{ padding: '6px 10px' }}>PVE %</th>
                    <th style={{ padding: '6px 10px' }}>Add</th>
                    <th style={{ padding: '6px 10px' }}>Stable</th>
                  </tr>
                </thead>
                <tbody>
                  {(polyReport.bkagrid_output?.published_qtls || []).map((q, i) => (
                    <tr key={`poly-qtl-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{q.name}</td>
                      <td style={{ padding: '6px 10px' }}>{q.trait}</td>
                      <td style={{ padding: '6px 10px', fontWeight: 600 }}>{q.chromosome}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.position_cM).toFixed(1)}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.lod).toFixed(2)}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.pve_percent).toFixed(1)}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.add_effect).toFixed(2)}</td>
                      <td style={{ padding: '6px 10px' }}>{q.stable ? '✓' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>

            {/* Findings */}
            {polyReport.findings?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-poly-findings-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Findings &amp; methodological notes
                </h3>
                {polyReport.findings.map((f, i) => (
                  <div key={`poly-finding-${i}`} style={{
                    background: '#EFF6FF', padding: 12, borderRadius: 6, marginBottom: 8,
                    borderLeft: '3px solid #3B82F6', fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>[{f.severity}] {f.title}</div>
                    <div style={{ color: COLORS.textMedium, marginBottom: 4 }}>{f.detail}</div>
                    {f.implication && (
                      <div style={{ fontSize: 12, fontStyle: 'italic', color: COLORS.textMedium }}>
                        <strong>Implication:</strong> {f.implication}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Limitations */}
            <div style={{ background: COLORS.amber50, padding: 14, borderRadius: 8, borderLeft: `3px solid ${COLORS.amber}`, fontSize: 12, color: COLORS.amber900 }} data-testid="validation-poly-limitations">
              <strong>Honesty disclaimer:</strong>
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20, lineHeight: 1.6 }}>
                {(polyReport.limitations || []).map((l, i) => <li key={`poly-lim-${i}`}>{l}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Module 5 — Polyploid (AUTOtetraploid Potato, polysomic) on B2721 */}
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 12,
        padding: 24, marginBottom: 24, background: 'white',
      }} data-testid="validation-module-polyploid-potato">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 className="font-display" style={{ fontSize: 20, color: COLORS.green900, marginBottom: 2 }}>
              Module 5 — Polyploid (AUTOtetraploid Potato · polysomic) QTL Mapping
            </h2>
            <p style={{ fontSize: 13, color: COLORS.textMedium }}>
              Validated against B2721 (Atlantic × B1829-5) — da Silva Pereira et al. 2020;{' '}
              <strong>genuinely polysomic dosage {`{0…4}`}</strong> math
            </p>
          </div>
          {potReport && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge
                pass={potReport.overall_pass}
                label={`${potReport.n_passed}/${potReport.n_total_checks} ${potReport.overall_pass ? 'PASS' : 'CHECKS'}`}
              />
              <button
                onClick={fetchPotReport}
                disabled={potLoading}
                title="Re-run Polyploid Potato validation"
                style={{ padding: 6, border: `1px solid ${COLORS.border}`, background: 'white', borderRadius: 6, cursor: potLoading ? 'wait' : 'pointer' }}
                data-testid="validation-pot-refresh-btn"
              >
                {potLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </button>
            </div>
          )}
        </div>

        {potError && (
          <div style={{ padding: 12, background: COLORS.red50, color: COLORS.red, borderRadius: 6, marginBottom: 16 }}>
            <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            {potError}
          </div>
        )}

        {potLoading && !potReport && (
          <div style={{ padding: 24, textAlign: 'center', color: COLORS.textMedium }}>
            <Loader2 size={20} className="animate-spin" style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            Simulating a tetrasomic F1 family and recovering the chr-5 maturity QTL — typically &lt; 3 seconds...
          </div>
        )}

        {potReport && (
          <>
            <div style={{ background: '#F8FAFC', padding: 14, borderRadius: 8, marginBottom: 18, borderLeft: `3px solid ${COLORS.green500}` }} data-testid="validation-pot-dataset-card">
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>Dataset:</strong> {potReport.dataset?.dataset_full_name}</div>
                <div><strong>Crop:</strong> {potReport.dataset?.crop}</div>
                <div><strong>Population:</strong> {potReport.dataset?.population} · {potReport.dataset?.genotyping}</div>
                <div><strong>Map:</strong> {potReport.dataset?.map}</div>
                <div><strong>Method:</strong> {potReport.dataset?.method}</div>
                <div><strong>Reference:</strong> {potReport.dataset?.institute}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Citation:</strong> {potReport.dataset?.primary_citation}{' '}
                  <a href={potReport.dataset?.article_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.green500, textDecoration: 'underline' }}>
                    QTLpoly tutorial <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </a>
                </div>
              </div>
            </div>

            {potReport.bkagrid_output?.potato_qtl_map_png && (
              <div style={{ marginBottom: 18 }} data-testid="validation-pot-map-figure">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Autotetraploid potato QTL map + recovered dosage LOD profile (chr 5)
                </h3>
                <img
                  src={`data:image/png;base64,${potReport.bkagrid_output.potato_qtl_map_png}`}
                  alt="Autotetraploid potato QTL map"
                  style={{ width: '100%', maxWidth: 980, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}
                />
              </div>
            )}

            {potReport.sanity_invariants?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-pot-sanity-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Sanity invariants — polysomic dosage correctness
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px' }}>Check</th>
                      <th style={{ padding: '8px 10px' }}>Expected</th>
                      <th style={{ padding: '8px 10px' }}>Observed</th>
                      <th style={{ padding: '8px 10px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {potReport.sanity_invariants.map((s, i) => <SanityRow key={`pot-sanity-${i}`} row={s} />)}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginBottom: 18 }} data-testid="validation-pot-comparisons-section">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                Recovery &amp; structural comparisons vs B2721 published values
                <span style={{ fontWeight: 400, fontSize: 12, color: COLORS.textMedium, marginLeft: 8 }}>
                  (chr-5 FM07 maturity QTL recovered by dosage LOD scan)
                </span>
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Quantity</th>
                    <th style={{ padding: '8px 10px' }}>BKAgriD</th>
                    <th style={{ padding: '8px 10px' }}>Published</th>
                    <th style={{ padding: '8px 10px' }}>Δ</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {potReport.comparisons.map((c, i) => <ComparisonRow key={`pot-cmp-${i}`} row={c} />)}
                </tbody>
              </table>
            </div>

            <details style={{ marginBottom: 18 }} data-testid="validation-pot-qtl-catalogue">
              <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                Published QTL catalogue — REMIM (random-effect) &amp; FEIM (dosage LOD)
              </summary>
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMedium, marginBottom: 4 }}>REMIM (score statistic)</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 14 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '6px 10px' }}>Trait</th>
                      <th style={{ padding: '6px 10px' }}>Chr</th>
                      <th style={{ padding: '6px 10px' }}>Pos (cM)</th>
                      <th style={{ padding: '6px 10px' }}>Score</th>
                      <th style={{ padding: '6px 10px' }}>P-value</th>
                      <th style={{ padding: '6px 10px' }}>s.int (cM)</th>
                      <th style={{ padding: '6px 10px' }}>h²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(potReport.bkagrid_output?.remim_qtls || []).map((q, i) => (
                      <tr key={`pot-remim-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: '6px 10px' }}>{q.trait}</td>
                        <td style={{ padding: '6px 10px', fontWeight: 600 }}>{q.chromosome}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.position_cM).toFixed(1)}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.score).toFixed(2)}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{q.pval}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{q.sint_lower_cM}–{q.sint_upper_cM}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.h2).toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMedium, marginBottom: 4 }}>FEIM (dosage LOD)</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '6px 10px' }}>Trait</th>
                      <th style={{ padding: '6px 10px' }}>Chr</th>
                      <th style={{ padding: '6px 10px' }}>Pos (cM)</th>
                      <th style={{ padding: '6px 10px' }}>LOD</th>
                      <th style={{ padding: '6px 10px' }}>Adj R²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(potReport.bkagrid_output?.feim_qtls || []).map((q, i) => (
                      <tr key={`pot-feim-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: '6px 10px' }}>{q.trait}</td>
                        <td style={{ padding: '6px 10px', fontWeight: 600 }}>{q.chromosome}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.position_cM).toFixed(1)}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.lod).toFixed(2)}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{Number(q.adj_r2).toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            {potReport.findings?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-pot-findings-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Findings &amp; methodological notes
                </h3>
                {potReport.findings.map((f, i) => (
                  <div key={`pot-finding-${i}`} style={{ background: '#EFF6FF', padding: 12, borderRadius: 6, marginBottom: 8, borderLeft: '3px solid #3B82F6', fontSize: 13 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>[{f.severity}] {f.title}</div>
                    <div style={{ color: COLORS.textMedium, marginBottom: 4 }}>{f.detail}</div>
                    {f.implication && (
                      <div style={{ fontSize: 12, fontStyle: 'italic', color: COLORS.textMedium }}>
                        <strong>Implication:</strong> {f.implication}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ background: COLORS.amber50, padding: 14, borderRadius: 8, borderLeft: `3px solid ${COLORS.amber}`, fontSize: 12, color: COLORS.amber900 }} data-testid="validation-pot-limitations">
              <strong>Honesty disclaimer:</strong>
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20, lineHeight: 1.6 }}>
                {(potReport.limitations || []).map((l, i) => <li key={`pot-lim-${i}`}>{l}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Module 6 — Augmented MET Scenario B on Belamkar (2018) winter wheat */}
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 12,
        padding: 24, marginBottom: 24, background: 'white',
      }} data-testid="validation-module-scenario-b">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 className="font-display" style={{ fontSize: 20, color: COLORS.green900, marginBottom: 2 }}>
              Module 6 — Augmented MET Scenario B (Federer Combining Over Sites)
            </h2>
            <p style={{ fontSize: 13, color: COLORS.textMedium }}>
              Germplasm-screening structure: different unreplicated test entries per site,
              common checks anchor the sites. Validated against an independent hand-derivation
              on the Belamkar (2018) Nebraska winter-wheat augmented design.
            </p>
          </div>
          {sbReport && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge
                pass={sbReport.overall_pass}
                label={`${sbReport.n_passed}/${sbReport.n_total_checks} ${sbReport.overall_pass ? 'PASS' : 'CHECKS'}`}
              />
              <button
                onClick={fetchSbReport}
                disabled={sbLoading}
                title="Re-run Scenario B validation"
                style={{ padding: 6, border: `1px solid ${COLORS.border}`, background: 'white', borderRadius: 6, cursor: sbLoading ? 'wait' : 'pointer' }}
                data-testid="validation-sb-refresh-btn"
              >
                {sbLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </button>
            </div>
          )}
        </div>

        {sbError && (
          <div style={{ padding: 12, background: COLORS.red50, color: COLORS.red, borderRadius: 6, marginBottom: 16 }}>
            <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            {sbError}
          </div>
        )}

        {sbLoading && !sbReport && (
          <div style={{ padding: 24, textAlign: 'center', color: COLORS.textMedium }}>
            <Loader2 size={20} className="animate-spin" style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            Running check-anchoring analysis on the Belamkar 2018 screening layout — typically &lt; 3 seconds...
          </div>
        )}

        {sbReport && (
          <>
            {/* Dataset provenance */}
            <div style={{ background: '#F8FAFC', padding: 14, borderRadius: 8, marginBottom: 18, borderLeft: `3px solid ${COLORS.green500}` }} data-testid="validation-sb-dataset-card">
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>Dataset:</strong> {sbReport.dataset?.dataset_full_name}</div>
                <div><strong>Population:</strong> {sbReport.dataset?.population}</div>
                <div><strong>Environments:</strong> {sbReport.dataset?.environments}</div>
                <div><strong>Design:</strong> {sbReport.dataset?.design}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Primary citation:</strong> {sbReport.dataset?.primary_citation}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMedium }}>
                  <strong>Article:</strong>{' '}
                  <a href={sbReport.dataset?.article_url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.green500, textDecoration: 'underline' }}>
                    doi:{sbReport.dataset?.doi} <ExternalLink size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </a>
                </div>
              </div>
            </div>

            {/* Sanity invariants */}
            {sbReport.sanity_invariants?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-sb-sanity-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Sanity invariants — must hold for ANY correct Scenario B analysis
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px' }}>Check</th>
                      <th style={{ padding: '8px 10px' }}>Expected</th>
                      <th style={{ padding: '8px 10px' }}>Observed</th>
                      <th style={{ padding: '8px 10px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sbReport.sanity_invariants.map((s, i) => <SanityRow key={`sb-san-${i}`} row={s} />)}
                  </tbody>
                </table>
              </div>
            )}

            {/* Numerical comparisons vs hand-derivation */}
            <div style={{ marginBottom: 18 }} data-testid="validation-sb-comparisons-section">
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                Check-anchoring comparisons{' '}
                <span style={{ fontWeight: 400, fontSize: 12, color: COLORS.textMedium }}>
                  — BKAgriD engine vs independent numpy re-derivation of Federer&apos;s formula (Pattern 13)
                </span>
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Quantity</th>
                    <th style={{ padding: '8px 10px' }}>BKAgriD</th>
                    <th style={{ padding: '8px 10px' }}>Reference</th>
                    <th style={{ padding: '8px 10px' }}>Δ</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sbReport.comparisons.map((c, i) => <ComparisonRow key={`sb-cmp-${i}`} row={c} />)}
                </tbody>
              </table>
            </div>

            {/* BKAgriD output — env effects + top ranking */}
            <details style={{ marginBottom: 18 }} data-testid="validation-sb-output-details">
              <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                BKAgriD engine output (environment effects + check-adjusted ranking)
              </summary>
              <div style={{ background: '#EFF6FF', padding: 12, borderRadius: 6, marginTop: 8, fontSize: 12, lineHeight: 1.6 }}>
                {sbReport.bkagrid_output?.provenance_banner}
              </div>
              <div style={{ marginTop: 10, fontSize: 13 }}>
                <strong>Grand check mean:</strong>{' '}
                <span style={{ fontFamily: 'monospace' }} data-testid="validation-sb-grand-check-mean">{sbReport.bkagrid_output?.grand_check_mean}</span> bu/acre
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px' }}>Environment</th>
                    <th style={{ padding: '6px 10px' }}>Check Mean</th>
                    <th style={{ padding: '6px 10px' }}>Env Effect</th>
                    <th style={{ padding: '6px 10px' }}># Tests</th>
                  </tr>
                </thead>
                <tbody>
                  {(sbReport.bkagrid_output?.env_effects || []).map((e, i) => (
                    <tr key={`sb-env-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '6px 10px' }}>{e.Env}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{e.env_check_mean}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{e.env_effect}</td>
                      <td style={{ padding: '6px 10px' }}>{e.n_tests}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: COLORS.green900 }}>Top check-adjusted test entries (combining over sites)</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px' }}>Rank</th>
                    <th style={{ padding: '6px 10px' }}>Genotype</th>
                    <th style={{ padding: '6px 10px' }}>Environment</th>
                    <th style={{ padding: '6px 10px' }}>Raw</th>
                    <th style={{ padding: '6px 10px' }}>Adjusted</th>
                  </tr>
                </thead>
                <tbody>
                  {(sbReport.bkagrid_output?.scenario_b_ranking_top10 || []).map((r, i) => (
                    <tr key={`sb-rank-${i}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '6px 10px', fontWeight: 600 }}>{r.Rank}</td>
                      <td style={{ padding: '6px 10px' }}>{r.Genotype}</td>
                      <td style={{ padding: '6px 10px', color: COLORS.textMedium }}>{r.Env}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: COLORS.textMedium }}>{r.Raw_Mean}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{r.CrossEnv_Adjusted_Mean}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>

            {/* Findings */}
            {sbReport.findings?.length > 0 && (
              <div style={{ marginBottom: 18 }} data-testid="validation-sb-findings-section">
                <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.green900, marginBottom: 8 }}>
                  Findings &amp; methodological notes
                </h3>
                {sbReport.findings.map((f, i) => (
                  <div key={`sb-finding-${i}`} style={{ background: '#EFF6FF', padding: 12, borderRadius: 6, marginBottom: 8, borderLeft: '3px solid #3B82F6', fontSize: 13 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>[{f.severity}] {f.title}</div>
                    <div style={{ color: COLORS.textMedium, marginBottom: 4 }}>{f.detail}</div>
                    {f.implication && (
                      <div style={{ fontSize: 12, fontStyle: 'italic', color: COLORS.textMedium }}>
                        <strong>Implication:</strong> {f.implication}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ background: COLORS.amber50, padding: 14, borderRadius: 8, borderLeft: `3px solid ${COLORS.amber}`, fontSize: 12, color: COLORS.amber900 }} data-testid="validation-sb-limitations">
              <strong>Honesty disclaimer:</strong>
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20, lineHeight: 1.6 }}>
                {(sbReport.limitations || []).map((l, i) => <li key={`sb-lim-${i}`}>{l}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
